import React, { useEffect, useRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Minus, Type, Table } from 'lucide-react';

interface RichTextEditorProps {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const isInternalUpdate = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editorRef.current && !isInternalUpdate.current) {
            if (editorRef.current.innerHTML !== value) {
                editorRef.current.innerHTML = value || '';
            }
        }
        isInternalUpdate.current = false;
    }, [value]);

    const handleInput = () => {
        if (editorRef.current) {
            isInternalUpdate.current = true;
            onChange(editorRef.current.innerHTML);
        }
    };

    const exec = (cmd: string, val?: string) => {
        document.execCommand(cmd, false, val);
        if (editorRef.current) editorRef.current.focus();
        handleInput();
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    exec('insertImage', event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const addTable = () => {
        const tableHTML = '<br><table border="1" cellpadding="5" cellspacing="0" style="width:100%; border-collapse: collapse; border-color: #cbd5e1;"><tbody><tr><td><br></td><td><br></td></tr><tr><td><br></td><td><br></td></tr></tbody></table><br>';
        exec('insertHTML', tableHTML);
    };

    const addLink = () => {
        const url = prompt("Link URL:");
        if (url) exec('createLink', url);
    };

    return (
        <div className="w-full border border-slate-200 rounded-[1.5rem] overflow-hidden bg-white focus-within:ring-2 focus-within:ring-red-0 transition-all flex flex-col h-96 shadow-sm">
            <div className="flex items-center gap-1 p-2 border-b border-slate-100 bg-slate-50/50 flex-wrap">
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
                    <button onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Bold"><Bold size={14} /></button>
                    <button onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Italic"><Italic size={14} /></button>
                    <button onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Underline"><Underline size={14} /></button>
                </div>
                <div className="w-px h-6 bg-slate-300 mx-1"></div>
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5 items-center px-2">
                    <select onChange={(e) => { e.preventDefault(); exec('fontName', e.target.value); }} className="text-xs bg-transparent outline-none cursor-pointer text-slate-600 font-bold" title="Font">
                        <option value="Arial">Arial</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Inter">Inter</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Tahoma">Tahoma</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Georgia">Georgia</option>
                    </select>
                </div>
                <div className="w-px h-6 bg-slate-300 mx-1"></div>
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
                    <button onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'H2'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all font-bold text-xs" title="Heading">H1</button>
                    <button onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'H3'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all font-bold text-xs" title="Subheading">H2</button>
                </div>
                <div className="w-px h-6 bg-slate-300 mx-1"></div>
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
                    <button onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Bullet List"><List size={14} /></button>
                    <button onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Numbered List"><ListOrdered size={14} /></button>
                </div>
                <div className="w-px h-6 bg-slate-300 mx-1"></div>
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
                    <button onMouseDown={(e) => { e.preventDefault(); addLink(); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Link"><LinkIcon size={14} /></button>
                    <input type="file" accept="image/*" ref={fileInputRef} className="hidden" onChange={handleImageUpload} />
                    <button onMouseDown={(e) => { e.preventDefault(); fileInputRef.current?.click(); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Upload Image"><ImageIcon size={14} /></button>
                    <button onMouseDown={(e) => { e.preventDefault(); addTable(); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Insert Table"><Table size={14} /></button>
                    <button onMouseDown={(e) => { e.preventDefault(); exec('insertHorizontalRule'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Line"><Minus size={14} /></button>
                </div>
            </div>
            <div
                ref={editorRef}
                className="flex-1 p-6 outline-none overflow-y-auto text-sm leading-relaxed text-slate-900 font-medium [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_li]:mb-1 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-1 [&_a]:text-indigo-600 [&_a]:underline"
                contentEditable
                onInput={handleInput}
                suppressContentEditableWarning
                data-placeholder={placeholder}
                style={{ fontFamily: 'Arial, sans-serif' }}
            />
        </div>
    );
};
